<!-- SwipFx Orders Page (Bootstrap 5) -->
<!-- Tabs need bootstrap.bundle.js -->
<div class="min-vh-100 bg-light">
    <app-header [page]="'Admin'"></app-header>
<main class="container py-4">

  <div class="d-flex justify-content-between align-items-center mb-3">
    <div>
      <h4 class="fw-bold mb-0">Tier Limits</h4>
      <div class="text-muted small">
        Configure activity limits for this compliance tier
      </div>
    </div>

    <button class="btn btn-outline-secondary btn-sm" (click)="cancel()">
      Back
    </button>
  </div>

  <div class="card shadow-sm border-0 mb-4" *ngIf="tier">
    <div class="card-body">
      <div class="row g-3 small">
        <div class="col-md-3">
          <div class="text-muted">Tier</div>
          <div class="fw-semibold">{{ tier.name }}</div>
        </div>

        <div class="col-md-2">
          <div class="text-muted">Tier Code</div>
          <div class="fw-semibold">{{ tier.tierCode }}</div>
        </div>

        <div class="col-md-2">
          <div class="text-muted">Jurisdiction</div>
          <div class="fw-semibold">{{ tier.jurisdictionCode || tier.jurisdictionName }}</div>
        </div>

        <div class="col-md-2">
          <div class="text-muted">Customer</div>
          <div class="fw-semibold">{{ tier.customerType }}</div>
        </div>

        <div class="col-md-3">
          <div class="text-muted">Rank</div>
          <div class="fw-semibold">{{ tier.rank }}</div>
        </div>
      </div>
    </div>
  </div>

  <div *ngIf="loading" class="text-center py-4">
    <div class="spinner-border"></div>
  </div>

  <div *ngIf="error" class="alert alert-danger">
    {{ error }}
  </div>

  <form [formGroup]="form" (ngSubmit)="save()" class="card shadow-sm border-0 mb-4">
    <div class="card-body">
      <h5 class="mb-3">
        {{ editingId ? 'Edit Limit' : 'New Limit' }}
      </h5>

      <div class="row g-3">
        <div class="col-md-3">
          <label class="form-label">Activity Type</label>
          <select class="form-select" formControlName="activityType">
            <option *ngFor="let item of activityTypes" [ngValue]="item.value">
              {{ item.label }}
            </option>
          </select>

          <div class="small text-danger mt-1" *ngIf="activityType?.touched && activityType?.invalid">
            Activity type is required.
          </div>
        </div>

        <div class="col-md-3">
          <label class="form-label">Currency</label>
          <select class="form-select"
                  formControlName="currency">
            <option value="">
              {{ loadingCurrencies ? 'Loading currencies...' : 'Select currency' }}
            </option>
            <option *ngFor="let item of currencyOptions" [value]="item.currency.code">
              {{ currencyLabel(item) }}
            </option>
          </select>

          <div class="small text-danger mt-1" *ngIf="currency?.touched && currency?.invalid">
            Currency is required.
          </div>
          <div class="small text-muted mt-1" *ngIf="!loadingCurrencies && tier?.jurisdictionCode && !currencyOptions.length">
            No currencies are configured for {{ tier?.jurisdictionCode }}.
          </div>
        </div>

        <div class="col-md-2">
          <label class="form-label">Per Transaction</label>
          <input class="form-control"
                 type="number"
                 min="0"
                 formControlName="perTransactionLimit"
                 placeholder="0.00" />
        </div>

        <div class="col-md-2">
          <label class="form-label">Daily</label>
          <input class="form-control"
                 type="number"
                 min="0"
                 formControlName="dailyLimit"
                 placeholder="0.00" />
        </div>

        <div class="col-md-2">
          <label class="form-label">Monthly</label>
          <input class="form-control"
                 type="number"
                 min="0"
                 formControlName="monthlyLimit"
                 placeholder="0.00" />
        </div>

        <div class="col-md-3">
          <label class="form-label d-block">Unlimited</label>
          <div class="form-check form-switch mt-2">
            <input class="form-check-input" type="checkbox" formControlName="isUnlimited" />
            <label class="form-check-label">Yes</label>
          </div>
        </div>

        <div class="col-md-3">
          <label class="form-label d-block">Allowed</label>
          <div class="form-check form-switch mt-2">
            <input class="form-check-input" type="checkbox" formControlName="isAllowed" />
            <label class="form-check-label">Yes</label>
          </div>
        </div>
      </div>

      <div class="mt-4 d-flex gap-2">
        <button class="btn btn-primary" type="submit" [disabled]="saving || loadingCurrencies || !currencyOptions.length">
          <span *ngIf="saving" class="spinner-border spinner-border-sm me-2"></span>
          {{ editingId ? 'Update Limit' : 'Create Limit' }}
        </button>

        <button class="btn btn-outline-secondary" type="button" (click)="resetForm()">
          Clear
        </button>
      </div>
    </div>
  </form>

  <div *ngIf="!loading && !limits.length" class="alert alert-light border">
    No tier limits found.
  </div>

  <div *ngIf="limits.length" class="card shadow-sm border-0">
    <div class="table-responsive">
      <table class="table table-hover align-middle mb-0">
        <thead class="table-light">
          <tr>
            <th>Activity</th>
            <th>Currency</th>
            <th>Per Transaction</th>
            <th>Daily</th>
            <th>Monthly</th>
            <th>Unlimited</th>
            <th>Allowed</th>
            <th></th>
          </tr>
        </thead>

        <tbody>
          <tr *ngFor="let item of limits">
            <td>{{ getActivityTypeLabel(item.activityType) }}</td>
            <td>{{ item.currency }}</td>
            <td>{{ item.perTransactionLimit ?? '-' }}</td>
            <td>{{ item.dailyLimit ?? '-' }}</td>
            <td>{{ item.monthlyLimit ?? '-' }}</td>

            <td>
              <span class="badge" [ngClass]="badgeClass(item.isUnlimited)">
                {{ asYesNo(item.isUnlimited) }}
              </span>
            </td>

            <td>
              <span class="badge" [ngClass]="badgeClass(item.isAllowed)">
                {{ asYesNo(item.isAllowed) }}
              </span>
            </td>

            <td class="text-end">
              <div class="d-inline-flex gap-2">
                <button class="btn btn-sm btn-outline-primary" (click)="edit(item)">
                  Edit
                </button>

                <button class="btn btn-sm btn-outline-danger"
                        [disabled]="deletingId === item.id"
                        (click)="delete(item)">
                  <span *ngIf="deletingId === item.id" class="spinner-border spinner-border-sm me-2"></span>
                  Delete
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>

  <app-footer></app-footer>
</main>
</div>
